import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { apiErrorMsg } from "../lib/api";
import {
  User, Mail, Phone, ArrowRight, Home, Building2, Hammer, CheckCircle, Sparkles,
} from "lucide-react";
import {
  FloatingLabelInput, PasswordInput, GradientButton, AuthSplitLeft,
  AuthHeader, AuthFooterNote, TrustBadgeStrip,
} from "../components/AuthUI";
import GoogleSignInButton from "../components/GoogleSignInButton";
import usePageTitle from "../lib/usePageTitle";

const ROLE_DESCRIPTIONS = {
  client: { title: "Client", icon: Home, copy: "Find verified properties with complete transparency.", color: "from-[#EEF4FF] to-white", iconCls: "bg-[#EEF4FF] text-[#0044CC]" },
  dealer: { title: "Dealer", icon: Building2, copy: "Manage listings, capture leads & dominate local markets.", color: "from-[#EEF4FF] to-white", iconCls: "bg-gradient-to-br from-[#0055FF] to-[#2D7BFF] text-white", popular: true },
  builder: { title: "Builder / Owner", icon: Hammer, copy: "Showcase projects & generate qualified enquiries.", color: "from-[#F5F3FF] to-white", iconCls: "bg-[#F5F3FF] text-[#6D28D9]" },
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const initialRole = (() => {
    const r = new URLSearchParams(loc.search).get("role");
    return ["client", "dealer", "builder"].includes(r) ? r : "client";
  })();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: initialRole });
  const [busy, setBusy] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  usePageTitle("Create Your Account | HouseEntry");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    // v2.43 — Phone now mandatory for ALL self-signup roles (client, dealer, builder)
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length < 10) {
      setPhoneError("Required · 10 digits minimum");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Account created — welcome, ${u.name}`);
      const queryNext = new URLSearchParams(loc.search).get("next");
      const fallback = u.role === "dealer" ? "/dealer"
        : u.role === "builder" ? "/builder"
        : u.role === "team" ? "/admin"
        : "/pricing";
      nav(queryNext || fallback);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Registration failed"));
    }
    setBusy(false);
  };

  const phoneRequired = true;  // v2.43 — phone mandatory for all roles

  return (
    <div className="grid lg:grid-cols-[1.05fr_1fr] min-h-screen bg-zinc-50" data-testid="register-page">
      <AuthSplitLeft
        eyebrow="HouseEntry · Join the Network"
        heading="Join 100+ verified dealers building their pipeline."
        sub="Get verified listings, masked client leads, and a CRM workspace tuned for Delhi NCR."
        quote="Within 2 weeks I closed 3 deals from masked leads — finally a serious platform."
        attribution="Verified Builder / Owner · West Delhi"
      />

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 relative">
        <div className="auth-blobs lg:hidden opacity-30" />
        <div className="w-full max-w-md relative">
          <AuthHeader showBack backTo="/access" />

          <div className="auth-glass rounded-2xl p-7 sm:p-8" data-testid="register-form">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#0055FF] mb-3 inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Create Account
            </div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-zinc-900">Create your account</h1>
            <p className="text-[14px] text-zinc-600 mt-1.5 mb-6">Choose your role to get the right workspace.</p>

            {/* Role selector — premium cards */}
            <div className="grid grid-cols-3 gap-2.5 mb-5" data-testid="role-selector">
              {Object.entries(ROLE_DESCRIPTIONS).map(([key, def]) => {
                const active = form.role === key;
                const Icon = def.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => update("role", key)}
                    className={`relative p-3.5 rounded-xl text-left transition-all duration-200 ${
                      active
                        ? "border-2 border-[#0055FF] bg-gradient-to-br from-[#EEF4FF] to-white shadow-md shadow-blue-500/15 -translate-y-0.5"
                        : "border-2 border-zinc-200 bg-white hover:border-[#0055FF]/40 hover:-translate-y-0.5"
                    }`}
                    data-testid={`role-${key}`}
                  >
                    {def.popular && (
                      <span className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 text-[8.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#0055FF] to-[#2D7BFF] text-white">
                        <Sparkles size={7} /> Popular
                      </span>
                    )}
                    <div className={`w-9 h-9 rounded-lg ${def.iconCls} flex items-center justify-center mb-2`}>
                      <Icon size={16} />
                    </div>
                    <div className={`font-bold text-[13.5px] ${active ? "text-[#0055FF]" : "text-zinc-900"}`}>{def.title}</div>
                    <div className="text-[10.5px] text-zinc-600 mt-0.5 leading-snug">{def.copy}</div>
                  </button>
                );
              })}
            </div>

            <form onSubmit={submit} className="space-y-3.5">
              <FloatingLabelInput
                id="register-name" label="Full name" icon={User}
                value={form.name} onChange={(e) => update("name", e.target.value)}
                required autoComplete="name" testid="register-name"
              />
              <FloatingLabelInput
                id="register-email" type="email" label="Email address" icon={Mail}
                value={form.email} onChange={(e) => update("email", e.target.value)}
                required autoComplete="email" testid="register-email"
              />
              <FloatingLabelInput
                id="register-phone" type="tel"
                label="Phone (required)"
                icon={Phone}
                value={form.phone}
                onChange={(e) => update("phone", e.target.value.replace(/[^\d+]/g, "").slice(0, 13))}
                inputMode="tel"
                required
                error={phoneError}
                hint="10 digits — used for callbacks & account recovery"
                testid="register-phone"
              />
              <PasswordInput
                value={form.password} onChange={(e) => update("password", e.target.value)}
                autoComplete="new-password" label="Password (min 6 chars)" testid="register-password"
              />

              <div className="pt-2">
                <GradientButton type="submit" loading={busy} testid="register-submit" icon={ArrowRight}>
                  Create My Account
                </GradientButton>
              </div>

              {/* Google Sign-In — Emergent-managed OAuth. See /app/auth_testing.md. */}
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center" aria-hidden>
                  <div className="w-full border-t border-zinc-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-[11px] uppercase tracking-widest font-semibold text-zinc-400">
                    or
                  </span>
                </div>
              </div>
              <GoogleSignInButton testid="register-google-btn" label="Sign up with Google" />
            </form>

            <div className="mt-5 pt-5 border-t border-zinc-200 text-center text-[12.5px] text-zinc-600">
              Already a member?{" "}
              <Link to="/login" className="text-[#0055FF] font-bold hover:underline" data-testid="link-login">Sign in →</Link>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <TrustBadgeStrip items={["Verified", "Encrypted", "Free to Join"]} />
            <AuthFooterNote />
          </div>
        </div>
      </div>
    </div>
  );
}
